import { Link, useParams, useSearchParams } from "react-router-dom";
import DeviceOnlineStateIcon from "../components/Devices/DeviceStateIcon";
import DeviceDetailModalMap from "../components/modals/locationModal/DeviceDetailModalMap";
import SoilChart from "../components/soilChart/SoilChart";
import TankChart from "../components/tankChart/TankChart";
import { useDeviceSettingsContext } from "../context/DeviceSettingsContext";
import { useDeviceMoistureReport } from "../hooks/useDeviceMoistureReport";
import { useDevice } from "../hooks/useDevices";
import { useDeviceTankReport } from "../hooks/useDeviceTankReport";
import { useNotifications } from "../hooks/useNotifications";
import strings from "../i18n/i18nStrings";
import { TNotification } from "../types/Notifications";
import { DetailPageNotification } from "../components/Notifications/DetailPageNotification";
import { Battery, BatteryFull, BatteryLow, BatteryMedium, Edit3 } from "lucide-react";
import { useEffect, useState } from "react";
import DevicePhotos, { FileStateType } from "../components/photos";
import { nanoid } from "nanoid";
import { StatusIndicator, MultiStatusIndicator } from "../components/StatusIndicator";

function BatteryLevel({ value }: { value?: number }) {
  if (!value) {
    return <>N/a</>;
  }

  if (value >= 90) {
    return (
      <span className="flex gap-2 items-center">
        <BatteryFull />
        <span>{value?.toFixed(0)}%</span>
      </span>
    );
  }

  if (value >= 50) {
    return (
      <span className="flex gap-2 items-center">
        <BatteryMedium />
        <span>{value?.toFixed(0)}%</span>
      </span>
    );
  }

  if (value >= 10) {
    return (
      <span className="flex gap-2 items-center">
        <BatteryLow />
        <span>{value?.toFixed(0)}%</span>
      </span>
    );
  }

  return (
    <span className="flex gap-2 items-center">
      <Battery />
      <span>{value?.toFixed(0)}%</span>
    </span>
  );
}

export default function DevicePage() {
  // const { events } = useDeviceEvents(device?.device_id);
  const { deviceId } = useParams<{ deviceId: string }>();
  const [searchParams] = useSearchParams();
  const { device, savePhotos } = useDevice(deviceId || null);
  const shouldOpenPhotos = searchParams.get('openPhotos') === 'true';

  const getTextColor = (backgroundColor: string): string => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return black text for light backgrounds, white text for dark backgrounds
    return brightness > 128 ? '#000000' : '#ffffff';
  };
  const { data: deviceNotifications } = useNotifications({
    device_id: device?.device_id,
    limit: 20,
    sort: "updated_at|desc",
  });
  const { events: moistureReport } = useDeviceMoistureReport(device?.device_id);
  const { events: tankReport } = useDeviceTankReport(device?.device_id);

  const [photos, setPhotos] = useState<FileStateType[]>([]);

  useEffect(() => {
    if (device?.photos) {
      setPhotos(
        device.photos?.map((p) => {
          // Check if p is an object with created_at or just a URL string
          if (typeof p === 'object' && p.created_at) {
            return {
              id: nanoid(),
              type: "server" as const,
              url: p.url,
              addedAt: new Date(p.created_at),
            };
          } else {
            // Extract timestamp from URL filename (e.g., 1752178253829-blob)
            const url = p as string;
            const timestampMatch = url.match(/\/(\d{13})-blob$/);
            let addedAt: Date | undefined = undefined;
            
            if (timestampMatch) {
              const timestamp = parseInt(timestampMatch[1]);
              addedAt = new Date(timestamp);
            }
            
            return {
              id: nanoid(),
              type: "server" as const,
              url: url,
              addedAt: addedAt,
            };
          }
        })
      );
    }
  }, [device?.photos]);


  const { onOpenDeviceModal } = useDeviceSettingsContext();
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row min-h-screen">
        <div className="md:hidden w-full mb-4">
        {device && device.lat && (
          <DeviceDetailModalMap
            selectedDevice={device}
            className="w-full h-48"
          />
        )}
      </div>

      {/* Left Column - Main Content */}
      <div className="w-full md:w-2/3 md:pr-8">
        <div className="max-w-[600px] mx-auto md:mx-0">
        <nav className="text-sm hidden md:block">
          <Link
            to="/"
            className="font-semibold underline text-blue-700 hover:text-blue-800"
          >
            Dashboard
          </Link>{" "}
          &gt;{" "}
          <span className="px-0 pr-3 font-semibold text-grey-900">
            {" "}
            {device.name}{" "}
          </span>
          <span className="text-xs text-gray-300 ">{device.device_id}</span>
        </nav>

        <div className="flex justify-between items-center my-4 md:my-6">
          <h1 className="text-2xl md:text-4xl font-bold">{device.name}</h1>
          <div className="flex items-baseline">
            <span className="text-sm font-semibold pr-5 text-green-800">
              <BatteryLevel value={device?.battery_percentage} />
            </span>
            <DeviceOnlineStateIcon
              updated_at={device?.last_event_updated_at}
              showLabel={false}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mb-4 md:mb-8">
          <div>
            {device?.location?.name && (
              <span 
                className="px-3 inline-flex text-sm leading-8 font-semibold rounded-full"
                style={{
                  backgroundColor: device?.location?.color || "#3B82F6",
                  color: getTextColor(device?.location?.color || "#3B82F6")
                }}
              >
                {device?.location?.name}
              </span>
            )}
          </div>
          <span
            onClick={() => onOpenDeviceModal(device)}
            className="ml-4 text-sm font-semibold underline text-blue-700 hover:text-blue-800 cursor-pointer"
          >
            Edit settings
          </span>
        </div>

        {/* Device Status Grid - 4 columns on desktop */}
        <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs md:text-sm">
          <div>
            <div className="relative inline-block">
              <span 
                className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b border-dashed border-gray-400 cursor-help" 
                onMouseEnter={() => setActiveTooltip('water-fill')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                CWM water level
              </span>
              {activeTooltip === 'water-fill' && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-md whitespace-nowrap z-10">
                  Water fill level in your CWM module
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
            <div className="pt-2 px-0">
              {device?.water_percentage < 0 || device?.tank_disconnected ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-gray-600 font-semibold text-sm">N/A</span>
                </div>
              ) : (
                <MultiStatusIndicator 
                  value={device.water_percentage}
                  thresholds={[
                    { value: 40, label: `${device.water_percentage}%`, color: "green" },
                    { value: 10, label: `${device.water_percentage}%`, color: "orange" },
                    { value: 0, label: `${device.water_percentage}%`, color: "red" }
                  ]}
                />
              )}
            </div>
          </div>
          <div>
            <div className="relative inline-block">
              <span 
                className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b border-dashed border-gray-400 cursor-help" 
                onMouseEnter={() => setActiveTooltip('refill-date')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                Est Refill date
              </span>
              {activeTooltip === 'refill-date' && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-md whitespace-nowrap z-10">
                  Estimated refill date based on usage patterns
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
            <div className="pt-2 px-0">
              {device?.estimated_refill_date ? (
                <div className="flex items-center gap-2">
                  {/* <div className="w-3 h-3 rounded-full bg-blue-500" /> */}
                  <span className="text-gray-900 font-semibold text-sm">
                    {new Date(device.estimated_refill_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-gray-600 font-semibold text-sm">N/A</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="relative inline-block">
              <span 
                className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b border-dashed border-gray-400 cursor-help" 
                onMouseEnter={() => setActiveTooltip('tank-status')}
                onMouseLeave={() => setActiveTooltip(null)}
              >
                CWM TANK STATUS
              </span>
              {activeTooltip === 'tank-status' && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-md whitespace-nowrap z-10">
                  The status of your connected CWM
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
            <div className="pt-2 px-0">
              {device.tank_disconnected ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-gray-900 font-semibold text-sm">Disconnected</span>
                </div>
              ) : device.tank_unsealed ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-gray-900 font-semibold text-sm">Unsealed</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-gray-900 font-semibold text-sm">No issues</span>
                </div>
              )}
            </div>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
              BATTERY LEVEL
            </span>
            <div className="pt-2 px-0">
              {device.battery_percentage !== undefined ? (
                <MultiStatusIndicator 
                  value={device.battery_percentage}
                  thresholds={[
                    { value: 40, label: "Good", color: "green" },
                    { value: 30, label: "Charge Soon", color: "orange" },
                    { value: 15, label: "Low", color: "red" },
                    { value: 0, label: "Critical", color: "red" }
                  ]}
                />
              ) : (
                <span className="text-sm">N/a</span>
              )}
            </div>
          </div>
          </div>
        </div>

        {/*<div className="text-xs md:text-sm mb-4">
          <span className="font-bold">DEVICE ID</span>
          <p>{device.device_id}</p>
        </div>*/}

        <div className="md:hidden mb-4">
          <div className="bg-white shadow rounded-lg p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-500 text-xs font-bold uppercase tracking-wider">NOTES</h2>
              <Edit3 
                className="w-4 h-4 text-blue-600 cursor-pointer hover:text-blue-700" 
                onClick={() => onOpenDeviceModal(device)}
              />
            </div>
            <p className="text-gray-900 text-sm">{device.notes}</p>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <DevicePhotos
              photos={photos}
              setPhotos={setPhotos}
              labelStyle="text-gray-500 text-xs font-bold uppercase tracking-wider mb-4"
              onSave={async (updatedPhotos: FileStateType[]) =>
                await savePhotos(updatedPhotos)
              }
              standalone
              deviceId={device?.device_id}
              accountId={device?.account_id}
            />
          </div>

          {/* <div className="mt-6">
            <h2 className="font-bold mb-2">PHOTOS</h2>
            <div className="flex flex-wrap gap-2">
              <div className=" bg-slate-200 w-40 h-48"></div>
              <div className=" bg-slate-200 w-40 h-48"></div>
              <div className=" bg-slate-200 w-40 h-48"></div>
            </div>
          </div> */}
        </div>

        <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-6">
          <h2 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-4">
            EVENTS LOG
          </h2>
          <div className="mt-2 space-y-2">
            {[...(deviceNotifications || [])]
              .sort((a, b) => {
                // Always sort by updated_at in descending order (newest first)
                const dateA = new Date(a.updated_at || a.created_at);
                const dateB = new Date(b.updated_at || b.created_at);
                return dateB.getTime() - dateA.getTime();
              })
              .slice(0, 5)
              .map(
                (notification: TNotification) => (
                  <DetailPageNotification
                    key={notification._id}
                    notification={notification}
                    onClick={() => {}}
                  />
                )
              )}
          </div>
        </div>
        
        {/* Environmental Stats Grid - 3 columns */}
        <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-6">
          <div className="grid grid-cols-3 gap-4 text-xs md:text-sm">
          <div>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
              SUN
            </span>
            <p className="pt-2 px-0 font-semibold text-gray-900 text-sm">
              {device.light_level}
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
              TEMP
            </span>
            <p className="pt-2 px-0 font-semibold text-gray-900 text-sm">
              {device.temp?.toFixed(0) || "N/a"}°F
            </p>
          </div>
          <div>
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider whitespace-nowrap">
              HUMIDITY
            </span>
            <p className="pt-2 px-0 font-semibold text-gray-900 text-sm">
              {device.humidity?.toFixed(0) || "N/a"}%
            </p>
          </div>
          </div>
        </div>
        
        {/* Graphs */}
        <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-6">
          <h3 className="text-gray-500 text-xs mb-2 font-bold uppercase tracking-wider whitespace-nowrap">
          CWM water level history
          </h3>
          <p className="px-0 text-left text-gray-900 text-sm mb-2"> Water levels may naturally fluctuate throughout the day.</p>
          <div className="w-full justify-center">
            <TankChart
              chartData={tankReport.map((r) => ({
                moisture: r.water_percentage,
                timestamp: r.timestamp as unknown as string,
              }))}
            />
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-8">
          <h3 className="text-gray-500 text-xs mb-2 font-bold uppercase tracking-wider whitespace-nowrap">
            Soil Moisture History
          </h3>
          <div className="w-full justify-center">
            <SoilChart
              chartData={moistureReport.map((r) => ({
                moisture: r.moisture_ref,
                timestamp: r.timestamp as unknown as string,
              }))}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Right Column - Map */}
      <div className="w-full md:w-1/3 md:pl-4">
        {/* Location: <pre>{JSON.stringify(device.location, null, 2)}</pre> */}
        <div className="hidden md:block">
          {device && device.lat && (
            <DeviceDetailModalMap
              selectedDevice={device}
              className="w-full h-96"
            />
          )}
        </div>

        {/* Notes */}
        <div className="hidden md:block">
          <div className="bg-white shadow rounded-lg p-6 mb-4 md:mb-6 mt-4 md:mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-gray-500 text-xs font-bold uppercase tracking-wider">NOTES</h2>
              <Edit3 
                className="w-4 h-4 text-blue-600 cursor-pointer hover:text-blue-700" 
                onClick={() => onOpenDeviceModal(device)}
              />
            </div>
            <p className="text-gray-900 text-sm">{device.notes}</p>
          </div>

          <div className="bg-white shadow rounded-lg p-6">
            <DevicePhotos
              photos={photos}
              setPhotos={setPhotos}
              labelStyle="text-gray-500 text-xs font-bold uppercase tracking-wider mb-4"
              onSave={async (updatedPhotos: FileStateType[]) =>
                await savePhotos(updatedPhotos)
              }
              standalone
              deviceId={device?.device_id}
              accountId={device?.account_id}
              autoOpenFirst={shouldOpenPhotos}
            />
          </div>

          {/* <div className="mt-6">
            <h2 className="font-bold mb-2">PHOTOS</h2>
            <div className="flex flex-wrap gap-2">
              <div className=" bg-slate-200 w-40 h-48"></div>
              <div className=" bg-slate-200 w-40 h-48"></div>
            </div>
          </div> */}
        </div>

        {/* {device && !device.lat && (
          <div className="w-full h-60 bg-gray-300 flex items-center justify-center">
            <p>No location set</p>
          </div>
        )} */}
      </div>
    </div>
    </div>
  );
}
